import type { TFunction } from 'i18next'
import type { GymInsights, InsightMonth } from './types'

export type AnswerTone = 'good' | 'warn' | 'info'

export interface Answer {
  tone: AnswerTone
  /** Bold lead-in, then the rest of the sentence. */
  lead: string
  text: string
}

const revenueTotal = (m: InsightMonth) =>
  Object.values(m.revenue).reduce((a, b) => a + Number(b), 0)
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0)

/**
 * "This month in short": up to six plain sentences, each picked by a rule from
 * the same numbers the cards draw. The current month is still running, so the
 * comparisons use the last full month against the one before it.
 */
export function buildAnswers(
  d: GymInsights,
  t: TFunction,
  monthName: (ym: string) => string,
  money: (amount: number) => string,
): Answer[] {
  const out: Answer[] = []
  const full = d.months.slice(0, -1)
  const prev = full[full.length - 1]
  const before = full[full.length - 2]

  // Growing or not: members today against the end of last month.
  if (prev) {
    const diff = d.membersNow - prev.members
    const month = monthName(prev.month)
    if (diff > 0) {
      out.push({
        tone: 'good',
        lead: t('statistics.answers.growingLead'),
        text: t('statistics.answers.growing', { count: d.membersNow, diff, month }),
      })
    } else if (diff < 0) {
      out.push({
        tone: 'warn',
        lead: t('statistics.answers.shrinkingLead'),
        text: t('statistics.answers.shrinking', { count: d.membersNow, diff: -diff, month }),
      })
    } else {
      out.push({
        tone: 'info',
        lead: t('statistics.answers.steadyLead'),
        text: t('statistics.answers.steady', { count: d.membersNow, month }),
      })
    }
  }

  // Where new members come from, over the months shown.
  const src = d.months.reduce(
    (a, m) => ({
      app: a.app + m.sources.app,
      desk: a.desk + m.sources.desk,
      dayPass: a.dayPass + m.sources.dayPass,
    }),
    { app: 0, desk: 0, dayPass: 0 },
  )
  const joined = src.app + src.desk + src.dayPass
  if (joined > 0) {
    const appShare = pct(src.app, joined)
    out.push(
      appShare >= 40
        ? {
            tone: 'good',
            lead: t('statistics.answers.appLead'),
            text: t('statistics.answers.app', { pct: appShare }),
          }
        : {
            tone: 'info',
            lead: t('statistics.answers.deskLead'),
            text: t('statistics.answers.desk', { pct: pct(src.desk + src.dayPass, joined) }),
          },
    )
  }

  // Who has not been in.
  out.push(
    d.atRiskCount > 0
      ? {
          tone: 'warn',
          lead: t('statistics.answers.atRiskLead', { count: d.atRiskCount }),
          text: t('statistics.answers.atRisk', { count: d.atRiskCount }),
        }
      : {
          tone: 'good',
          lead: t('statistics.answers.noneAtRiskLead'),
          text: t('statistics.answers.noneAtRisk'),
        },
  )

  // First-timers coming back: the latest full month that had any.
  const fv = [...full].reverse().find((m) => m.firstVisits > 0)
  if (fv) {
    const share = pct(fv.cameBack, fv.firstVisits)
    out.push({
      tone: share >= 50 ? 'good' : 'warn',
      lead: t('statistics.answers.cameBackLead', { pct: share }),
      text: t('statistics.answers.cameBack', { month: monthName(fv.month) }),
    })
  }

  // Renewals: the last two full months that had plans ending.
  const withEnded = full.filter((m) => m.ended > 0)
  const r1 = withEnded[withEnded.length - 1]
  const r0 = withEnded[withEnded.length - 2]
  if (r1) {
    const now = pct(r1.renewed, r1.ended)
    const was = r0 ? pct(r0.renewed, r0.ended) : null
    if (was !== null && now < was - 1) {
      out.push({
        tone: 'warn',
        lead: t('statistics.answers.renewDippedLead'),
        text: t('statistics.answers.renewDipped', { pct: now, from: was }),
      })
    } else if (was !== null && now > was + 1) {
      out.push({
        tone: 'good',
        lead: t('statistics.answers.renewRoseLead'),
        text: t('statistics.answers.renewRose', { pct: now, from: was }),
      })
    } else {
      out.push({
        tone: 'info',
        lead: t('statistics.answers.renewSteadyLead', { pct: now }),
        text: t('statistics.answers.renewSteady', { month: monthName(r1.month) }),
      })
    }
  }

  // Revenue: last full month, the best of the range or against the one before.
  if (prev && revenueTotal(prev) > 0) {
    const amount = money(revenueTotal(prev))
    const month = monthName(prev.month)
    const best = full.every((m) => revenueTotal(m) <= revenueTotal(prev))
    if (best && full.length > 1) {
      out.push({
        tone: 'good',
        lead: t('statistics.answers.bestRevenueLead'),
        text: t('statistics.answers.bestRevenue', { amount, month }),
      })
    } else if (before && revenueTotal(before) > 0) {
      const change = Math.round(
        ((revenueTotal(prev) - revenueTotal(before)) / revenueTotal(before)) * 100,
      )
      out.push(
        change < 0
          ? {
              tone: 'warn',
              lead: t('statistics.answers.revenueDownLead'),
              text: t('statistics.answers.revenueDown', { amount, month, pct: -change }),
            }
          : {
              tone: 'good',
              lead: t('statistics.answers.revenueUpLead'),
              text: t('statistics.answers.revenueUp', { amount, month, pct: change }),
            },
      )
    }
  }

  return out
}
